/**
 * W-091 — Reference data: programs, streams (شعبة) and cities. Stories: US-101, US-A02.
 *
 * SPRINT 1 CRITICAL PATH. Signup asks for a stream and a city (docs/05 M-006);
 * until these two tables are populated, no student can create an account and
 * the mobile team is blocked. docs/05 §15 puts it plainly: "needed by day 5".
 *
 * Bilingual names, order, active state. Streams carry a stable `code` because
 * content and plans reference them by meaning, not by row id.
 */
import { DeleteButton, List, useModalForm, useTable } from '@refinedev/antd'
import { useCan } from '@refinedev/core'
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tabs,
  Typography,
} from 'antd'
import { useState } from 'react'
import { BilingualField, OrderableTable, StatusTag } from '@/components'
import { persistOrder } from '@/lib/order'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { City, Locale, Program, Stream } from '@/types'
import { usePrograms } from './usePrograms'

const ProgramsTab = () => {
  const translate = useAdminTranslate()
  const { tableProps, tableQueryResult } = useTable<Program>({
    resource: 'programs',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 100 },
  })
  const { data: canEdit } = useCan({ resource: 'programs', action: 'edit' })

  const createModal = useModalForm<Program>({
    resource: 'programs',
    action: 'create',
    redirect: false,
  })
  const editModal = useModalForm<Program>({
    resource: 'programs',
    action: 'edit',
    redirect: false,
  })

  const rows = (tableProps.dataSource ?? []) as Program[]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space>
        <Button type="primary" onClick={() => createModal.show()}>
          {translate('admin.screens.referenceData.addProgram')}
        </Button>
      </Space>

      <OrderableTable<Program>
        {...tableProps}
        dataSource={rows}
        reorderDisabled={!canEdit?.can}
        onReorder={ordered => {
          void persistOrder('programs', ordered)
            .then(() => tableQueryResult.refetch())
            .catch((error: unknown) => console.error('[programs] reorder failed', error))
        }}
        columns={[
          { title: translate('admin.common.code'), dataIndex: 'code' },
          {
            title: translate('admin.columns.nameAr'),
            dataIndex: 'name_ar',
            render: (value: string) => <span dir="rtl">{value}</span>,
          },
          { title: translate('admin.columns.nameFr'), dataIndex: 'name_fr' },
          {
            title: translate('admin.common.activeFem'),
            dataIndex: 'is_active',
            render: (value: boolean) => <StatusTag kind="published" value={value} />,
          },
          {
            title: translate('admin.columns.actions'),
            dataIndex: 'id',
            render: (_: unknown, record: Program) => (
              <Space>
                <Button size="small" onClick={() => editModal.show(record.id)}>
                  {translate('admin.common.edit')}
                </Button>
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  confirmTitle={translate('admin.screens.referenceData.deleteProgramConfirm')}
                  confirmOkText={translate('common.delete')}
                  confirmCancelText={translate('common.cancel')}
                />
              </Space>
            ),
          },
        ]}
      />

      <Modal
        {...createModal.modalProps}
        title={translate('admin.screens.referenceData.newProgram')}
        forceRender
      >
        <Form {...createModal.formProps} layout="vertical">
          <ProgramFields />
        </Form>
      </Modal>
      <Modal
        {...editModal.modalProps}
        title={translate('admin.screens.referenceData.editProgram')}
        forceRender
      >
        <Form {...editModal.formProps} layout="vertical">
          <ProgramFields />
        </Form>
      </Modal>
    </Space>
  )
}

const ProgramFields = () => {
  const translate = useAdminTranslate()

  return (
    <>
      <Form.Item
        name="code"
        label={translate('admin.common.code')}
        rules={[{ required: true, message: translate('admin.screens.referenceData.codeRequired') }]}
        help={translate('admin.screens.referenceData.programCodeHelp')}
      >
        <Input placeholder="baccalaureat" />
      </Form.Item>
      <BilingualField
        name="name"
        label={translate('admin.columns.name')}
        requiredAr
        maxLength={80}
      />
      <Form.Item name="sort_order" label={translate('admin.common.order')} initialValue={0}>
        <InputNumber min={0} />
      </Form.Item>
      <Form.Item
        name="is_active"
        label={translate('admin.common.activeFem')}
        valuePropName="checked"
        initialValue
      >
        <Switch />
      </Form.Item>
    </>
  )
}

const StreamsTab = () => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const { programs } = usePrograms()
  const { tableProps, tableQueryResult } = useTable<Stream>({
    resource: 'streams',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 100 },
  })
  const { data: canEdit } = useCan({ resource: 'streams', action: 'edit' })

  const createModal = useModalForm<Stream>({
    resource: 'streams',
    action: 'create',
    redirect: false,
  })
  const editModal = useModalForm<Stream>({
    resource: 'streams',
    action: 'edit',
    redirect: false,
  })

  const rows = (tableProps.dataSource ?? []) as Stream[]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space>
        <Button type="primary" onClick={() => createModal.show()}>
          {translate('admin.screens.referenceData.addStream')}
        </Button>
      </Space>

      <OrderableTable<Stream>
        {...tableProps}
        dataSource={rows}
        reorderDisabled={!canEdit?.can}
        onReorder={ordered => {
          void persistOrder('streams', ordered)
            .then(() => tableQueryResult.refetch())
            .catch((error: unknown) => console.error('[streams] reorder failed', error))
        }}
        columns={[
          { title: translate('admin.common.code'), dataIndex: 'code' },
          {
            title: translate('admin.columns.nameAr'),
            dataIndex: 'name_ar',
            render: (value: string) => <span dir="rtl">{value}</span>,
          },
          { title: translate('admin.columns.nameFr'), dataIndex: 'name_fr' },
          {
            title: translate('admin.columns.program'),
            dataIndex: 'program_id',
            render: (value: string) => programLabel(programs, value, locale),
          },
          {
            title: translate('admin.common.activeFem'),
            dataIndex: 'is_active',
            render: (value: boolean) => <StatusTag kind="published" value={value} />,
          },
          {
            title: translate('admin.columns.actions'),
            dataIndex: 'id',
            render: (_: unknown, record: Stream) => (
              <Space>
                <Button size="small" onClick={() => editModal.show(record.id)}>
                  {translate('admin.common.edit')}
                </Button>
                <DeleteButton
                  hideText
                  size="small"
                  recordItemId={record.id}
                  confirmTitle={translate('admin.screens.referenceData.deleteStreamConfirm')}
                  confirmOkText={translate('common.delete')}
                  confirmCancelText={translate('common.cancel')}
                />
              </Space>
            ),
          },
        ]}
      />

      <Modal
        {...createModal.modalProps}
        title={translate('admin.screens.referenceData.newStream')}
        forceRender
      >
        <Form {...createModal.formProps} layout="vertical">
          <StreamFields />
        </Form>
      </Modal>
      <Modal
        {...editModal.modalProps}
        title={translate('admin.screens.referenceData.editStream')}
        forceRender
      >
        <Form {...editModal.formProps} layout="vertical">
          <StreamFields />
        </Form>
      </Modal>
    </Space>
  )
}

const StreamFields = () => {
  const translate = useAdminTranslate()
  const { programs, isLoading } = usePrograms()

  return (
    <>
      <Form.Item
        name="program_id"
        label={translate('admin.columns.program')}
        rules={[
          { required: true, message: translate('admin.screens.referenceData.programRequired') },
        ]}
      >
        <Select
          loading={isLoading}
          optionFilterProp="label"
          options={programs.map(program => ({
            value: program.id,
            label: `${program.name_fr} — ${program.name_ar}`,
          }))}
        />
      </Form.Item>
      <Form.Item
        name="code"
        label={translate('admin.common.code')}
        rules={[{ required: true, message: translate('admin.screens.referenceData.codeRequired') }]}
        help={translate('admin.screens.referenceData.codeHelp')}
      >
        <Input placeholder="sciences_math" />
      </Form.Item>
      <BilingualField
        name="name"
        label={translate('admin.columns.name')}
        requiredAr
        maxLength={80}
      />
      <Form.Item name="sort_order" label={translate('admin.common.order')} initialValue={0}>
        <InputNumber min={0} />
      </Form.Item>
      <Form.Item
        name="is_active"
        label={translate('admin.common.activeFem')}
        valuePropName="checked"
        initialValue
      >
        <Switch />
      </Form.Item>
    </>
  )
}

const CitiesTab = () => {
  const translate = useAdminTranslate()
  const { tableProps, tableQueryResult } = useTable<City>({
    resource: 'cities',
    sorters: { initial: [{ field: 'sort_order', order: 'asc' }] },
    pagination: { pageSize: 100 },
  })
  const { data: canEdit } = useCan({ resource: 'cities', action: 'edit' })

  const createModal = useModalForm<City>({ resource: 'cities', action: 'create', redirect: false })
  const editModal = useModalForm<City>({ resource: 'cities', action: 'edit', redirect: false })

  const rows = (tableProps.dataSource ?? []) as City[]

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Button type="primary" onClick={() => createModal.show()}>
        {translate('admin.screens.referenceData.addCity')}
      </Button>

      <OrderableTable<City>
        {...tableProps}
        dataSource={rows}
        reorderDisabled={!canEdit?.can}
        onReorder={ordered => {
          void persistOrder('cities', ordered)
            .then(() => tableQueryResult.refetch())
            .catch((error: unknown) => console.error('[cities] reorder failed', error))
        }}
        columns={[
          {
            title: translate('admin.columns.nameAr'),
            dataIndex: 'name_ar',
            render: (value: string) => <span dir="rtl">{value}</span>,
          },
          { title: translate('admin.columns.nameFr'), dataIndex: 'name_fr' },
          {
            title: translate('admin.columns.actions'),
            dataIndex: 'id',
            render: (_: unknown, record: City) => (
              <Space>
                <Button size="small" onClick={() => editModal.show(record.id)}>
                  {translate('admin.common.edit')}
                </Button>
                <DeleteButton hideText size="small" recordItemId={record.id} />
              </Space>
            ),
          },
        ]}
      />

      <Modal
        {...createModal.modalProps}
        title={translate('admin.screens.referenceData.newCity')}
        forceRender
      >
        <Form {...createModal.formProps} layout="vertical">
          <BilingualField
            name="name"
            label={translate('admin.columns.name')}
            requiredAr
            maxLength={80}
          />
          <Form.Item name="sort_order" label={translate('admin.common.order')} initialValue={0}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        {...editModal.modalProps}
        title={translate('admin.screens.referenceData.editCity')}
        forceRender
      >
        <Form {...editModal.formProps} layout="vertical">
          <BilingualField
            name="name"
            label={translate('admin.columns.name')}
            requiredAr
            maxLength={80}
          />
          <Form.Item name="sort_order" label={translate('admin.common.order')}>
            <InputNumber min={0} />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  )
}

export const ReferenceDataPage = () => {
  const translate = useAdminTranslate()
  const [tab, setTab] = useState('streams')

  return (
    <List title={translate('admin.pages.referenceData')} canCreate={false}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="warning"
          showIcon
          message={translate('admin.screens.referenceData.criticalTitle')}
          description={translate('admin.screens.referenceData.criticalBody')}
        />
        <Typography.Text type="secondary">
          {translate('admin.screens.referenceData.bilingualNotice')}
        </Typography.Text>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            {
              key: 'programs',
              label: translate('admin.screens.referenceData.programsTab'),
              children: <ProgramsTab />,
            },
            {
              key: 'streams',
              label: translate('admin.screens.referenceData.streamsTab'),
              children: <StreamsTab />,
            },
            {
              key: 'cities',
              label: translate('admin.screens.referenceData.citiesTab'),
              children: <CitiesTab />,
            },
          ]}
        />
      </Space>
    </List>
  )
}

function programLabel(programs: Program[], id: string, locale: Locale): string {
  const program = programs.find(item => item.id === id)
  if (!program) return id.slice(0, 8)
  return locale === 'ar' ? program.name_ar : program.name_fr
}
